import { APIClient, HTTPClient, HTTPClientCaches, HTTPClientConfig, HTTPClientNetwork } from '@innerhive-internal/innerhive-api-client';
import { AnonymousViewerSessionManager } from './AnonymousViewerSessionManager';
import { ICareMap, ICareMapKey } from '../../caremap/ICareMap';
import jwtDecode from 'jwt-decode';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../../support/AsyncStorageProvider';
import { nanoid } from 'nanoid';

const CAREMAP_MEDIA_TYPE = 'application/vnd.innerhive.caremap.v3+json';

type IEpochSeconds = number;

interface IBasicCaremapInfoForError {
    patientName: string;
}

interface ICompositeAnonymousHttpClientStorageState {
    sessionToBasicCaremapInfo: {
        [sessionKey: string]: IBasicCaremapInfoForError
    }
    anonymousUserId: string;
}

interface IClientConfig {
    client: HTTPClient;
    session_token: string;
    access_token: string;
    careMap?: ICareMap;
}

export class ViewerJWTExpiredError extends Error {
    constructor(careMaps: IBasicCaremapInfoForError[]){
        super('one or more sessions tokens were expired');
        this.expiredCareMaps = careMaps;
    }
    errorCode = 'viewer.jwt.expired';
    expiredCareMaps?: IBasicCaremapInfoForError[];
}

export class AnonymousClientStorageProvider {
    storage: IAsyncSmartStorageProvider<ICompositeAnonymousHttpClientStorageState>;

    static inject = () => [AsyncStorageProvider];
    constructor(provider: AsyncStorageProvider) {
        this.storage = provider.getJSONProvider<ICompositeAnonymousHttpClientStorageState>('CompositeAnonymousHttpClient', { envNeutral: false, userNeutral: true });
    }
}

export class CompositeAnonymousHttpClient {
    static inject = () => [
        HTTPClientConfig,
        AnonymousViewerSessionManager,
        AnonymousClientStorageProvider,
        HTTPClientNetwork,
        HTTPClientCaches
    ];

    constructor(
        private config: HTTPClientConfig,
        private anonymousViewerSessionManager: AnonymousViewerSessionManager,
        private storageProvider: AnonymousClientStorageProvider,
        private network: HTTPClientNetwork,
        private caches: HTTPClientCaches
    ){}

    private sessionToBasicCaremapInfo!: ICompositeAnonymousHttpClientStorageState['sessionToBasicCaremapInfo'];

    public anonymousUserId!: string; // set in init

    private urlPrefixToClient: { [urlPrefix: string]: IClientConfig} = {};

    private defaultClient?: HTTPClient;

    // Reused across all session-exchange calls so APIClient's per-instance entry cache
    // avoids a redundant GET / for each session token in the init loop.
    private anonymousApiClient?: APIClient;

    partiallyFailedError?: ViewerJWTExpiredError;

    private getHttpClientForUrl = (url: string): HTTPClient => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        let matchingPrefix = prefixes.find(prefix => url.startsWith(prefix));
        if (!matchingPrefix) {
            matchingPrefix = prefixes.find(prefix => prefix === '*');
        }
        if(!matchingPrefix){
            if(!this.defaultClient){
                throw new Error('we no longer have a default client, the user needs to restart, which will redirect them to login');
            }

            return this.defaultClient;
        }
        return this.urlPrefixToClient[matchingPrefix].client;
    };

    private cleanUpDeadHttpClient = async (deadClient: HTTPClient) => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        const matchingPrefix = prefixes.find(p => this.urlPrefixToClient[p].client === deadClient)!;

        const deadSessionToken = this.urlPrefixToClient[matchingPrefix].session_token;
        await this.anonymousViewerSessionManager.removeSession(deadSessionToken);

        delete this.urlPrefixToClient[matchingPrefix];
        if (this.defaultClient === deadClient){
            this.defaultClient = undefined;
            if (Object.keys(this.urlPrefixToClient).length > 0){
                const firstPrefix = Object.keys(this.urlPrefixToClient)[0];
                this.defaultClient = this.urlPrefixToClient[firstPrefix].client;
            }
        }
    };

    getInitCaremaps = (): ICareMap[] => {
        return Object.keys(this.urlPrefixToClient).map(prefix => this.urlPrefixToClient[prefix].careMap).filter(Boolean) as ICareMap[];
    };

    getCaremapForSession = (session_token: string) => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        const matchingPrefix = prefixes.find(p => this.urlPrefixToClient[p].session_token === session_token)!;
        if (!matchingPrefix){
            throw new Error(`no matching prefix for ${session_token}. which should not be possible`);
        }
        return this.urlPrefixToClient[matchingPrefix].careMap;
    };

    init = async () => {
        const fromStorage = await this.storageProvider.storage.get();
        this.sessionToBasicCaremapInfo = fromStorage?.sessionToBasicCaremapInfo || {};
        this.anonymousUserId = fromStorage?.anonymousUserId || `anonymous-${nanoid()}`;

        if (this.network && !this.network.isOnline) {
            const client = new HTTPClient(this.config, this.network, this.caches);
            if (!await client.isCached('/')) {
                // minimal caches required for app to start offline as anonymous user
                // (these need to be kept in sync with api responses)
                await client.cache('/', { user: { id: 'anonymous-default', sub: this.anonymousUserId, tokenType: 'caremap-viewer' }, links: { resources: { href: '/resources' }, sessionExchange: { href: '/sessions/[[session_token]]?anonymousUserId=[[anonymousUserId]]' } } });
                await client.cache(`/sessions/default?anonymousUserId=${this.anonymousUserId}`, { access_token: 'dummy-offline-token' });
            }
        }

        const failedTokens: string[] = [];

        for (const session_token of this.anonymousViewerSessionManager.getSessions().slice(0)){
            try {
                const access_token = await this.getAccessToken(session_token);

                const careMap = await this.getCareMap({ access_token });

                let previousSessionConfig: IClientConfig | undefined;
                if (careMap) {
                    previousSessionConfig = this.getSessionConfigForCareMap(careMap);
                } else {
                    previousSessionConfig = this.getSessionConfigForWildcard();
                }

                if (previousSessionConfig) {
                    const previousSessionToken = previousSessionConfig.session_token;

                    const isThisOneNewer = this.getAccessTokenExpiration(previousSessionConfig.access_token) > this.getAccessTokenExpiration(access_token);

                    if (isThisOneNewer){
                        this.anonymousViewerSessionManager.replaceSessionForSameCaremap({
                            older: previousSessionToken,
                            newer: session_token
                        });
                        // when we add this one, it will stomp on the old one
                        // b/c they have the same prefix
                        this.addClient({ access_token, session_token, careMap });
                    }
                    else {
                        this.anonymousViewerSessionManager.replaceSessionForSameCaremap({
                            older: session_token,
                            newer: previousSessionToken
                        });
                        // we don't need to change anything here, b/c the prefix is already registered, with the newer
                        // access token
                    }
                }
                else {
                    this.addClient({ access_token, careMap, session_token });
                }
            }
            catch(err: any){
                if (err && err.errorCode === 'viewer.jwt.expired'){
                    this.anonymousViewerSessionManager.removeSession(session_token);
                    failedTokens.push(session_token);

                } else {
                    throw err;
                }
            }
        }

        await this.cacheKnownCaremapsForFuturePageLoads();

        if (failedTokens.length > 0){
            const error = new ViewerJWTExpiredError(failedTokens.map(session_token => this.sessionToBasicCaremapInfo[session_token]).filter(info => Boolean(info)));
            if (this.getInitCaremaps().length > 0){
                this.partiallyFailedError = error;
            } else {
                throw error;
            }
        }
    };

    reset = async () => {
        this.urlPrefixToClient = {};
        this.defaultClient = undefined;
        await this.init();
    };

    private cacheKnownCaremapsForFuturePageLoads = async () => {

        for (const prefix of Object.keys(this.urlPrefixToClient)){
            const { session_token, careMap } = this.urlPrefixToClient[prefix];
            if (careMap) {
                this.sessionToBasicCaremapInfo[session_token] = {
                    patientName: this.getPatientName(careMap)
                };
            }
        }

        await this.storageProvider.storage.set({
            sessionToBasicCaremapInfo: this.sessionToBasicCaremapInfo,
            anonymousUserId: this.anonymousUserId
        });
    };

    private getPatientName = (careMap: ICareMap) => {
        const patient = careMap.patient;
        const first = patient.firstName || '';
        const last = patient.lastName || '';
        let name = first;
        if (last){
            const space = first ? ' ' : '';
            name = `${first}${space}${last}`;
        }
        return name;
    };

    getSessionConfigForCareMap = (careMapKey: ICareMapKey) => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        const matchingPrefix = prefixes.find(p => this.urlPrefixToClient[p].careMap?.userId === careMapKey.userId && this.urlPrefixToClient[p].careMap?.careMapId === careMapKey.careMapId);
        if (!matchingPrefix) {
            return undefined;
        }
        return this.urlPrefixToClient[matchingPrefix];
    };

    getSessionConfigForWildcard = () => {
        return this.urlPrefixToClient['*'];
    };

    getAccessTokenExpirationForCaremap = (careMapKey: ICareMapKey) => {
        const matchingConfig = this.getSessionConfigForCareMap(careMapKey);
        if (!matchingConfig){
            throw new Error(`no config yet for ${JSON.stringify(careMapKey)}`);
        }
        const access_token = matchingConfig.access_token;

        return this.getAccessTokenExpiration(access_token);
    };

    private getAccessTokenExpiration = (access_token: string): IEpochSeconds => {
        const decoded = jwtDecode(access_token) as { exp?: IEpochSeconds };

        return decoded.exp || Number.MAX_VALUE;
    };

    getSessionTokenByCaremap = (careMapKey: ICareMapKey) => {
        const matchingConfig = this.getSessionConfigForCareMap(careMapKey);
        if (!matchingConfig){
            throw new Error(`no config yet for ${JSON.stringify(careMapKey)}`);
        }
        return matchingConfig.session_token;
    };

    private addClient = ({ access_token, session_token, careMap }: { access_token: string; session_token: string; careMap?: ICareMap; }) => {
        const client = new HTTPClient(
            { ...this.config, accessToken: access_token },
            this.network,
            this.caches
        );

        const urlPrefix = (careMap ? `/users/${careMap.userId}/caremaps/${careMap.careMapId}` : '*');

        this.urlPrefixToClient[urlPrefix] = {
            client,
            session_token,
            access_token,
            careMap
        };

        // want the last (most recent) client to be the default client
        // so that it is what is used for entry resource on app start
        // and so we can quickly surface that it is expired (if it is)
        this.defaultClient = client;
    };

    onRequesting = (...params: any) => {
        // TODO: we could wire them all up, but I don't think we use this for anonynous so YAGNI
        throw new Error('not supported');
    };
    request = async ({ url, method, data, headers, options }: any) => {
        const httpClient = this.getHttpClientForUrl(url);
        try {
            return await httpClient.request({ url, method, data, headers, options });
        }
        catch (err: any){
            if (err && err.errorCode === 'viewer.jwt.expired'){
                // then this sessipn / token is dead and we need to remove it
                await this.cleanUpDeadHttpClient(httpClient);
                throw err;
            } else if (err && err.status === 401) {
                // this can happen if we reconnect after generating a dummy offline token,
                // in which case we need to reinitialize and retry
                await this.reset();
                const newClient = this.getHttpClientForUrl(url);
                return await newClient.request({ url, method, data, headers, options });
            } else {
                throw err;
            }
        }
    };

    delete = (url: string, headers?: any) => this.request({ url, method: 'DELETE', headers });
    patch = (url: string, data?: any, headers?: any) => this.request({ url, method: 'PATCH', data, headers });
    post = (url: string, data?: any, headers?: any) => this.request({ url, method: 'POST', data, headers });
    put = (url: string, data?: any, headers?: any) => this.request({ url, method: 'PUT', data, headers });
    get = (url: string, headers?: any, options?: any) => this.request({ url, method: 'GET', headers, options });


    private getAccessToken = async (session_token: string): Promise<string> => {
        if (!this.anonymousApiClient) {
            this.anonymousApiClient = new APIClient(new HTTPClient(this.config, this.network, this.caches));
        }
        const apiClient = this.anonymousApiClient;

        try {
            const entry = await apiClient.entry();
            const sessionExchange = entry?.links?.sessionExchange;
            if (!sessionExchange) {
                throw new Error('sessionExchange link missing from anonymous entry');
            }
            const response = await apiClient.get(sessionExchange, undefined, {
                session_token,
                anonymousUserId: this.anonymousUserId
            });
            return response.access_token;
        }
        catch (err: any){
            // as well (since it also converts sessions)
            // it will be redundant, but since this is both old ui and new ui, easier to never use
            // CompositeAnonymousHttpClient in the old UI and accept this duplication for now (IMO)
            if (err.status === 404){
                // it may not have ever existed, but that should only be true for a malicious user
                // so lets assume it did exist but is now expired
                err.errorCode = 'viewer.jwt.expired';
            }
            throw err;
        }

    };

    private getCareMap = async ({ session_token, access_token }: { session_token?: string; access_token?: string; }) => {
        if (!access_token && !session_token){
            throw new Error('one of session_token, access_token is required');
        }

        if (session_token && !access_token){
            access_token = await this.getAccessToken(session_token);
        }

        const client = new HTTPClient(
            { ...this.config, accessToken: access_token },
            this.network,
            this.caches
        );
        const api = new APIClient(client);

        const entry = await api.entry();

        if (!entry.links.careMaps) {
            return undefined;
        }

        const caremaps = await api.get(entry.links.careMaps, CAREMAP_MEDIA_TYPE);
        return caremaps[0] as (ICareMap & { links: { promoteFromAnonymous: any }});
    };
}
